import React from 'react';
import { Pagination as BsPagination } from 'react-bootstrap';

export interface PaginationProps {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  maxButtons?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  maxButtons = 5
}) => {
  // Toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(totalCount / pageSize);

  if (totalPages <= 1 && totalCount > 0) return null;

  // Sayfa butonlarını oluştur
  const createPaginationItems = () => {
    const items = [];

    // İlk sayfa butonu her zaman görünür
    items.push(
      <BsPagination.Item
        key="first"
        onClick={() => onPageChange(1)}
        active={currentPage === 1}
      >
        1
      </BsPagination.Item>
    );

    // maxButtons'dan fazla sayfa varsa ve aktif sayfa başlangıç veya son'dan uzaksa
    // bazı sayfa butonlarını "..." ile atla
    if (totalPages > maxButtons) {
      let startPage: number;
      let endPage: number;

      // Aktif sayfa başlangıca yakınsa
      if (currentPage < maxButtons - 1) {
        startPage = 2;
        endPage = Math.min(maxButtons, totalPages - 1);

        // Sayfalar arası
        for (let i = startPage; i <= endPage; i++) {
          items.push(
            <BsPagination.Item
              key={i}
              onClick={() => onPageChange(i)}
              active={currentPage === i}
            >
              {i}
            </BsPagination.Item>
          );
        }

        // Son sayfadan önce "..." göster
        if (totalPages > maxButtons) {
          items.push(<BsPagination.Ellipsis key="ellipsis-end" disabled />);
        }
      }
      // Aktif sayfa sona yakınsa
      else if (currentPage > totalPages - maxButtons + 2) {
        // Başlangıçta "..." göster
        items.push(<BsPagination.Ellipsis key="ellipsis-start" disabled />);

        startPage = Math.max(2, totalPages - maxButtons + 2);
        endPage = totalPages - 1;

        // Sayfalar arası
        for (let i = startPage; i <= endPage; i++) {
          items.push(
            <BsPagination.Item
              key={i}
              onClick={() => onPageChange(i)}
              active={currentPage === i}
            >
              {i}
            </BsPagination.Item>
          );
        }
      }
      // Aktif sayfa ortadaysa
      else {
        // Başlangıçta "..." göster
        items.push(<BsPagination.Ellipsis key="ellipsis-start" disabled />);

        // Ortadaki sayfalar
        const offset = Math.floor((maxButtons - 3) / 2);
        startPage = currentPage - offset;
        endPage = currentPage + offset;

        // Sayfalar arası
        for (let i = startPage; i <= endPage; i++) {
          items.push(
            <BsPagination.Item
              key={i}
              onClick={() => onPageChange(i)}
              active={currentPage === i}
            >
              {i}
            </BsPagination.Item>
          );
        }

        // Son sayfadan önce "..." göster
        items.push(<BsPagination.Ellipsis key="ellipsis-end" disabled />);
      }
    }
    // maxButtons veya daha az sayfa varsa tüm sayfaları göster
    else {
      for (let i = 2; i < totalPages; i++) {
        items.push(
          <BsPagination.Item
            key={i}
            onClick={() => onPageChange(i)}
            active={currentPage === i}
          >
            {i}
          </BsPagination.Item>
        );
      }
    }

    // Son sayfa butonu her zaman görünür (1'den fazla sayfa varsa)
    if (totalPages > 1) {
      items.push(
        <BsPagination.Item
          key="last"
          onClick={() => onPageChange(totalPages)}
          active={currentPage === totalPages}
        >
          {totalPages}
        </BsPagination.Item>
      );
    }

    return items;
  };

  return (
    <BsPagination>
      <BsPagination.Prev
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />

      {createPaginationItems()}

      <BsPagination.Next
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    </BsPagination>
  );
};

export default Pagination;